import React, { useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { ShieldCheck, ShieldAlert, Search, RefreshCw, AlertTriangle, CheckCircle2 } from "lucide-react";
import ClientPicker from "../components/ClientPicker";

function formatErr(detail) {
    if (!detail) return "Something went wrong.";
    if (typeof detail === "string") return detail;
    if (Array.isArray(detail)) return detail.map((e) => e?.msg || JSON.stringify(e)).join(" ");
    return String(detail);
}

export default function TenantBoundaryPage() {
    const [observerId, setObserverId] = useState("");
    const [targetId, setTargetId] = useState("");
    const [report, setReport] = useState(null);
    const [compliance, setCompliance] = useState(null);
    const [running, setRunning] = useState(false);
    const [loadingCompliance, setLoadingCompliance] = useState(false);

    const runTest = async () => {
        if (!targetId.trim()) {
            toast.error("Target client_id required");
            return;
        }
        setRunning(true);
        try {
            const { data } = await api.post(`/admin/tenancy/test`, {
                observer_client_id: observerId.trim() || null,
                target_client_id: targetId.trim(),
            });
            setReport(data);
        } catch (e) {
            toast.error(formatErr(e?.response?.data?.detail));
        } finally {
            setRunning(false);
        }
    };

    const loadCompliance = async () => {
        if (!targetId.trim()) {
            toast.error("Target client_id required");
            return;
        }
        setLoadingCompliance(true);
        try {
            const { data } = await api.get(`/admin/tenancy/compliance/${encodeURIComponent(targetId.trim())}`);
            setCompliance(data);
        } catch (e) {
            toast.error(formatErr(e?.response?.data?.detail));
        } finally {
            setLoadingCompliance(false);
        }
    };

    return (
        <div className="px-8 py-8" data-testid="tenant-boundary-page">
            <div className="flex items-start justify-between mb-8">
                <div>
                    <div className="label-xs text-[#7C3AED] flex items-center gap-2"><ShieldCheck className="w-3 h-3" /> Tenant Boundary</div>
                    <h1 className="font-display text-4xl tracking-tight mt-2">Cross-tenant Isolation Verifier</h1>
                    <p className="text-sm text-white/50 mt-2 max-w-2xl">
                        Simulate an observer scoped to <span className="text-emerald-300 font-mono">client_A</span> attempting
                        to read data owned by <span className="text-amber-300 font-mono">client_B</span>. A healthy boundary
                        returns <span className="text-emerald-300 font-mono">total_leaks = 0</span>.
                    </p>
                </div>
            </div>

            {/* Inputs */}
            <div className="bg-[#0a0a0a] border border-white/10 rounded-md p-5 mb-5 grid grid-cols-2 gap-4">
                <div>
                    <div className="label-xs mb-1.5">Observer client_id <span className="text-white/40">(optional — leave blank for "no tenant")</span></div>
                    <ClientPicker
                        value={observerId}
                        onChange={setObserverId}
                        anyOptionLabel="(no tenant — anonymous)"
                        includeSuperdomain
                        excludeDisabled
                        testId="tb-observer"
                    />
                </div>
                <div>
                    <div className="label-xs mb-1.5">Target client_id <span className="text-[#7C3AED]">*</span></div>
                    <ClientPicker
                        value={targetId}
                        onChange={setTargetId}
                        placeholder="(select target client…)"
                        includeSuperdomain
                        excludeDisabled
                        required
                        testId="tb-target"
                    />
                </div>
                <div className="col-span-2 flex justify-end gap-2">
                    <button onClick={loadCompliance} data-testid="tb-compliance-load"
                        disabled={loadingCompliance}
                        className="border border-white/15 text-white/85 px-4 py-2 rounded-md text-sm flex items-center gap-2 disabled:opacity-40">
                        {loadingCompliance ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />} Load compliance
                    </button>
                    <button onClick={runTest} data-testid="tb-run-test"
                        disabled={running}
                        className="bg-[#7C3AED] hover:bg-[#7C3AED]/90 text-white px-4 py-2 rounded-md text-sm flex items-center gap-2 disabled:opacity-40">
                        {running ? <RefreshCw className="w-4 h-4 animate-spin" /> : <ShieldCheck className="w-4 h-4" />} Run boundary test
                    </button>
                </div>
            </div>

            {/* Report */}
            {report && (
                <div className={`mb-6 rounded-md border p-5 ${report.healthy ? "border-emerald-500/30 bg-emerald-500/5" : "border-red-500/40 bg-red-500/10"}`} data-testid="tb-report">
                    <div className="flex items-center gap-3 mb-3">
                        {report.healthy ? <CheckCircle2 className="w-5 h-5 text-emerald-300" /> : <ShieldAlert className="w-5 h-5 text-red-300" />}
                        <div className="font-display text-xl tracking-tight">
                            {report.healthy ? "Boundary healthy — 0 leaks detected" : `${report.total_leaks} potential leak(s) detected`}
                        </div>
                    </div>
                    <div className="text-[11px] text-white/50 font-mono mb-4">
                        Observer: <span className="text-white/80">{report.observer_client_id || "(none)"}</span>
                        {" · "}Target: <span className="text-white/80">{report.target_client_id}</span>
                        {" · "}Run at {new Date(report.executed_at).toLocaleString()}
                    </div>
                    <table className="w-full">
                        <thead>
                            <tr className="text-left">
                                <th className="label-xs px-3 py-2">Entity</th>
                                <th className="label-xs px-3 py-2">Target owned</th>
                                <th className="label-xs px-3 py-2">Observer can see</th>
                                <th className="label-xs px-3 py-2">Leak</th>
                                <th className="label-xs px-3 py-2">Note</th>
                            </tr>
                        </thead>
                        <tbody>
                            {Object.entries(report.entities).map(([k, v]) => (
                                <tr key={k} className="border-t border-white/5" data-testid={`tb-entity-${k}`}>
                                    <td className="px-3 py-2 font-mono text-xs">{k}</td>
                                    <td className="px-3 py-2 font-mono text-xs text-white/70">{v.target_owned_total}</td>
                                    <td className="px-3 py-2 font-mono text-xs">{v.observer_can_see}</td>
                                    <td className="px-3 py-2">
                                        {v.leak ? (
                                            <span className="text-red-300 font-mono text-xs flex items-center gap-1"><AlertTriangle className="w-3 h-3" /> {v.leak}</span>
                                        ) : (
                                            <span className="text-emerald-300 font-mono text-xs">0</span>
                                        )}
                                    </td>
                                    <td className="px-3 py-2 text-[11px] text-white/45">{v.note || "—"}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            {/* Compliance */}
            {compliance && (
                <div className="bg-[#0a0a0a] border border-white/10 rounded-md p-5" data-testid="tb-compliance">
                    <div className="font-display text-xl tracking-tight mb-2">Compliance for <code className="text-[#c7a6ff]">{compliance.client_id}</code></div>
                    <div className="text-[11px] text-white/50 font-mono mb-4">As of {new Date(compliance.as_of).toLocaleString()}</div>

                    {/* Classification */}
                    <div className="grid grid-cols-3 gap-2 mb-4">
                        {Object.entries(compliance.classification_breakdown).map(([k, v]) => (
                            <div key={k} className="bg-[#050505] border border-white/10 rounded-md p-3">
                                <div className="label-xs">{k.replace(/_/g, " ")}</div>
                                <div className="font-display text-2xl mt-1">{v}</div>
                            </div>
                        ))}
                    </div>

                    {/* Retention */}
                    <div className="grid grid-cols-4 gap-2 mb-4">
                        {Object.entries(compliance.retention_breakdown).map(([k, v]) => (
                            <div key={k} className="bg-[#050505] border border-white/10 rounded-md p-3">
                                <div className="label-xs">{k.replace(/_/g, " ")}</div>
                                <div className="font-display text-xl mt-1">{v}</div>
                            </div>
                        ))}
                    </div>

                    {/* Per-entity */}
                    <div className="border-t border-white/10 pt-3">
                        <div className="label-xs mb-2">By entity type</div>
                        <table className="w-full">
                            <thead>
                                <tr className="text-left">
                                    <th className="label-xs px-3 py-2">Entity</th>
                                    <th className="label-xs px-3 py-2">Detail</th>
                                </tr>
                            </thead>
                            <tbody>
                                {Object.entries(compliance.entities).map(([k, v]) => (
                                    <tr key={k} className="border-t border-white/5">
                                        <td className="px-3 py-2 font-mono text-xs">{k}</td>
                                        <td className="px-3 py-2 font-mono text-[11px] text-white/70">{JSON.stringify(v)}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>

                    {/* Webhook queue health */}
                    <div className="border-t border-white/10 pt-3 mt-3">
                        <div className="label-xs mb-2">Webhook queue health</div>
                        <div className="grid grid-cols-6 gap-2">
                            {Object.entries(compliance.webhook_queue || {}).map(([s, n]) => (
                                <div key={s} className="bg-[#050505] border border-white/10 rounded-md p-2 text-center">
                                    <div className="label-xs">{s}</div>
                                    <div className="font-display text-base mt-1">{n}</div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
